import React, { useContext, useEffect, useMemo, useState } from "react";
import { Button, Spin, Switch, Tooltip } from "antd";
import { Select, message, Typography } from "antd";
import { PlusOutlined, SendOutlined, UnorderedListOutlined, InfoCircleOutlined, AudioOutlined } from "@ant-design/icons";
import { botOptions, getDynamicBotOptions, buildModelOptions, botIconsMap  } from "../../constants/chatPageConstants";
import { ThemeContext } from "../../contexts/themeConfig";
import UsefulPromptDropdown from "./UsefulPromptDropdown";
import { FaRegStopCircle } from "react-icons/fa";
import useRemaining from "../../Hooks/useRemaining";
import { useLocation, useNavigate } from "react-router-dom";
import { getUserID, getUserRole } from "../../Utility/service";
import { userSubscriptionInfo } from "../../api/profile";
import { useTour } from "../../Utility/walkthrough/useTour";
import { updateUserCompanyKnowledgeBaseUseFlag } from "../../api/user";
import SpeechRecognition, { useSpeechRecognition } from "react-speech-recognition";

const { Text } = Typography;
const SHORTCUT_PROMPT_TEXT = "Press '/' to access a list of task commands.";
const SHORTCUT_PROMPT_ERROR = "Please enter a prompt before selecting a command!";
const SEPARATOR_TIP = "Tip: Use exactly 4 dashes '----' as the separator to queue more than one message in one go";

const ChatPromptInputForm = ({
  states,
  actions,
  refs,
  showSelectors = true,
}) => {
  const {
    selectedTags,
    tags,
    loading = false,
    inputPrompt,
    showPromptDropdown,
    showMassage,
    setShowMassage,
    selectedCompany,
    selectedModel,
    setSelectedModel,
    currentChatId, // Add this to track current chat
  } = states;
  const { promptInputRef } = refs;
  const {
    onSubmit,
    handleSelectTags,
    setSelectedTags,
    selectedChatModel,
    setSelectedChatModel,
    setInputPrompt,
    setShowPromptDropdown,
    handleStopGeneratingButton,
    setSelectedCompany,
  } = actions;
  const { remaining, userInfo } = useRemaining();
  const { theme } = useContext(ThemeContext);
  const [defaultProvider, setDefaultProvider] = useState("");
  const { isDropdownOpen, setIsDropdownOpen, showTour, stepIndex } = useTour();

  const [dynamicBotOptions, setDynamicBotOptions] = useState([]);
  const [selectedOperationType, setSelectedOperationType] = useState(null); 
  const [fetchingOptions, setFetchingOptions] = useState(false);
  const [useBrandInfo, setUseBrandInfo] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const userId = getUserID()

  const {
    transcript,
    listening,
    resetTranscript,
    browserSupportsSpeechRecognition,
  } = useSpeechRecognition();

  const [basePrompt, setBasePrompt] = useState("");
  const location = useLocation();
  const [selectedBot, setSelectedBot] = useState(null);

  // Build final model options only when dynamicBotOptions is available
  const finalModelOptions = useMemo(() => {
    return buildModelOptions(dynamicBotOptions);
  }, [dynamicBotOptions]);

  const tagOptions = tags?.map((tag) => ({
    value: tag._id,
    label: tag.title,
  }));

  useEffect(() => {
    if(userInfo?.isUseCompanyKnowledgeBase){
      setUseBrandInfo(true);
    }else{
      setUseBrandInfo(false);
    }
  },[userInfo]);

  useEffect(() => {
    const fetchBotOptions = async () => {
      if (!userId) {
        return;
      }
      setFetchingOptions(true);
      try {
        const options = await getDynamicBotOptions(userId);
        setDynamicBotOptions(options);
      } catch (error) {
        console.error('[ChatPromptInputForm] Error fetching dynamic bot options:', error);
        setDynamicBotOptions([]);
      } finally {
        setFetchingOptions(false);
      }
    };
    fetchBotOptions();
  }, [userInfo?.id]);

  // Function to get chat-specific saved provider/model
  const getChatSpecificSelection = () => {
    if (currentChatId) {
      const chatProvider = localStorage.getItem(`chat_provider_${currentChatId}`);
      const chatModel = localStorage.getItem(`chat_model_${currentChatId}`);
      if (chatProvider && chatModel) {
        return { provider: chatProvider, model: chatModel };
      }
    }
    return null;
  };

  // Function to get global saved provider/model
  const getGlobalSelection = () => {
    const provider = localStorage.getItem("selectedBot");
    const model = localStorage.getItem("selectedModel");
    if (provider && model) {
      return { provider, model };
    }
    return null;
  };

  // Function to save selection both globally and chat-specifically
  const saveSelection = (provider, model) => {
    localStorage.setItem("selectedBot", provider);
    localStorage.setItem("selectedModel", model);
    
    if (currentChatId) {
      localStorage.setItem(`chat_provider_${currentChatId}`, provider);
      localStorage.setItem(`chat_model_${currentChatId}`, model);
    }
  };

  // Main initialization effect - runs after dynamicBotOptions are loaded
  useEffect(() => {
    if (fetchingOptions || !dynamicBotOptions.length || isInitialized) return;

    let providerToSet = null;
    let modelToSet = null;

    // Priority 1: Location state (coming from navigation)
    if (location.state?.selectedModel) {
      const selectedModelFromState = location.state.selectedModel;
      providerToSet = selectedModelFromState.name;
      modelToSet = selectedModelFromState.value;
      setSelectedOperationType(selectedModelFromState.inputOutputType);
    }
    // Priority 2: Chat-specific saved selection
    else {
      const chatSpecific = getChatSpecificSelection();
      if (chatSpecific) {
        providerToSet = chatSpecific.provider;
        modelToSet = chatSpecific.model;
      }
      // Priority 3: Global saved selection
      else {
        const globalSelection = getGlobalSelection();
        console.log("globalSelection : ",globalSelection);
        if (globalSelection) {
          providerToSet = globalSelection.provider;
          modelToSet = globalSelection.model;
        }
        // Priority 4: Default to OpenAI's first model
        else {
          providerToSet = 'openai';
          modelToSet = finalModelOptions.openai[0].value;
        }
      }
    }

    // Validate that the provider/model combination exists in finalModelOptions
    if (providerToSet && modelToSet) {
      const providerModels = finalModelOptions[providerToSet];
      const modelExists = providerModels?.some(model => model.value === modelToSet);
      
      if (modelExists) {
        setSelectedBot(providerToSet);
        setSelectedChatModel(providerToSet);
        setSelectedModel(modelToSet);
        saveSelection(providerToSet, modelToSet);
      } else {
        // Fallback to first available model for the provider
        if (providerModels?.[0]) {
          const fallbackModel = providerModels[0].value;
          setSelectedBot(providerToSet);
          setSelectedChatModel(providerToSet);
          setSelectedModel(fallbackModel);
          saveSelection(providerToSet, fallbackModel);
        } else {
          // Complete fallback to OpenAI's first model
          const defaultModel = finalModelOptions.openai[0].value;
          setSelectedBot('openai');
          setSelectedChatModel('openai');
          setSelectedModel(defaultModel);
          saveSelection('openai', defaultModel);
        }
      }
    }

    setIsInitialized(true);
  }, [dynamicBotOptions, fetchingOptions, location.state, currentChatId, finalModelOptions, isInitialized]);

  const handleBotChange = (value, option) => {
    const [provider, model] = value.split("|");
    console.log("value saving : ",value);
    
    setSelectedBot(provider);
    setSelectedChatModel(provider);
    setSelectedModel(model);
    
    saveSelection(provider, model);
  };

  const handleCompanyChange = (value) => {
    setSelectedCompany(value);
  };

  const handleTagChange = (selectedValues) => {
    const updatedSelectedTags = tags.filter((tag) =>
      selectedValues.includes(tag._id)
    );
    setSelectedTags(updatedSelectedTags);
    handleSelectTags(updatedSelectedTags);
  };

  const handleMicClick = () => {
    if (!browserSupportsSpeechRecognition) {
      message.error("Your browser does not support speech recognition.");
      return;
    }

    if (listening) {
      SpeechRecognition.stopListening();
      resetTranscript();
    } else {
      setBasePrompt(inputPrompt);
      resetTranscript();
      SpeechRecognition.startListening({ continuous: true });
    }
  };
  useEffect(() => {
    if (listening) {
      setInputPrompt(basePrompt + (basePrompt ? " " : "") + transcript);
      setShowMassage((basePrompt + transcript).includes("----"));
    }
  }, [transcript, listening, basePrompt, setInputPrompt, setShowMassage]);

  const onSubmitWrapper = (event, prompt = inputPrompt) => {
    if (listening) {
      SpeechRecognition.stopListening();
      resetTranscript();
    }
    onSubmit(event, prompt);
  };

  const handleKeyDown = (event) => {
    if (loading) return;
    if (event.key === "/") {
      setShowPromptDropdown(true);
    }
    if (event.key !== "/" && showPromptDropdown) {
      setShowPromptDropdown(false);
    }
    if (event.key === "Enter" && event.shiftKey) {
      setInputPrompt((prevValue) => prevValue);
    } else if (event.key === "Enter") {
      event.preventDefault();
      event.target.style.height = "51px";
      onSubmitWrapper(event);
    }
  };

  const onInputPromptChange = (event) => {
    const newValue = event.target.value;
    setInputPrompt(newValue);
    setBasePrompt(newValue);
    setShowMassage(newValue.includes("----"));
  };

  const handleUsefulPromptSelection = (selectedPrompt) => {
    const lastSlashRemoved = inputPrompt.replace(/\/$/, "");
    if (!lastSlashRemoved.trim()) {
      return message.warning(SHORTCUT_PROMPT_ERROR);
    }
    if (selectedPrompt && selectedPrompt?.label) {
      let userInputtedPrompt = `${lastSlashRemoved} ${selectedPrompt.label}`;
      setInputPrompt(userInputtedPrompt);
      setBasePrompt(userInputtedPrompt);
      setShowPromptDropdown(false);
      onSubmitWrapper(null, userInputtedPrompt);
    }
  };

  const scrollUp = () => {
    const textarea = promptInputRef.current;
    if (textarea) textarea.scrollBy(0, -30);
  };

  const scrollDown = () => {
    const textarea = promptInputRef.current;
    if (textarea) textarea.scrollBy(0, 30);
  };

  const getChatProviders = () => {
    if (userInfo?.role === "superadmin") {
      return botOptions;
    }
    const chatProviders = remaining?.chat_providers;
    const accessProviders = botOptions?.map((option) =>
      chatProviders?.includes(option.value) ? option : { ...option, disabled: true }
    );
    return accessProviders;
  };

  const getBotNameByValue = (value) => {
    switch (value) {
      case 'openai':
        return 'ChatGPT';
      case 'gemini':
        return 'Gemini';
      case 'claude':
        return 'Claude';
      case 'deepseek':
        return 'DeepSeek';
      default:
        return 'Assistant';
    }
  };

  const [activePlan, setActivePlan] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [visible, setVisible] = useState(true);
  const navigate = useNavigate();

  const userRole = getUserRole();

  const fetchData = async () => {
    setFetching(true);
    try {
      const subscriptionRes = await userSubscriptionInfo();
      setActivePlan(subscriptionRes?.activePlan);
    } catch (error) {
      console.error("Error fetching subscription info:", error);
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    let savedModel = null;
    if(getGlobalSelection()){
      const {provider,model} = getGlobalSelection();
      savedModel = `${provider}|${model}`
    }
    
      const value = savedModel? savedModel : botOptions.openai?.[0].value || "";
      setDefaultProvider(value);

  }, [remaining, userInfo, botOptions]);




  // Generate the current value for the Select component
  const currentSelectValue = useMemo(() => {
    if (selectedBot && selectedModel && finalModelOptions[selectedBot]) {
      const modelExists = finalModelOptions[selectedBot].some(model => model.value === selectedModel);
      if (modelExists) {
        return `${selectedBot}|${selectedModel}`;
      }
    }
    return undefined;
  }, [selectedBot, selectedModel, finalModelOptions]);

  useEffect(()=>{
    console.log("defaultProvider :",defaultProvider);

  },[defaultProvider])
  return (
    <div className="parent-container">
      <form
        className={`form-style ${theme === "dark" && "dark-mode"}`}
        onSubmit={onSubmitWrapper}
      >
        {showMassage && (
          <div className="query-massage">
            <p>
              <InfoCircleOutlined />
              <b className="ms-2 queue-title">{inputPrompt.split('----').filter(part => part.trim()).length} messages will be queued </b>
              <br />
              <small className="ms-4 queue-desc">{SEPARATOR_TIP}</small>
            </p>
          </div>
        )}

        <div
          className={`inputPromptTextarea-container ${theme === "dark" && "dark-mode"}`}
        >
          {!selectedCompany && (
            <div
              style={{
                marginBottom: "4px",
                marginTop: "4px",
                marginLeft: "6px",
                fontSize: "14px",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <Tooltip
                title="To update your company info, go to your profile page."
                placement="topLeft"
              >
                <InfoCircleOutlined style={{ fontSize: "16px", color: "#888" }} />
              </Tooltip>

              <div style={{ color: theme === "light" ? "rgb(87, 91, 95)" : "#fff" }}>
              This is a default {getBotNameByValue(selectedChatModel)} AI model. It has no context about your Company Information/Branding
              </div>
            </div>
          )}

          {showSelectors && (
            <div
              className={`select-container-whole ${theme === "dark" && "dark-mode"}`}
            >
              <div className="select-container">
              <Tooltip title="Choose AI and Model">
                  <Select
                    style={{ width: "40%" }}
                    placeholder="Choose Model"
                    onChange={handleBotChange}
                    value={currentSelectValue ? currentSelectValue : defaultProvider}
                    className="custom-select-bot responsive-select-bot"
                    placement="topLeft"
                    loading={fetchingOptions}
                    // defaultValue={defaultProvider}
                    open={showTour && stepIndex >= 0 ? isDropdownOpen : undefined}
                    onDropdownVisibleChange={(open) => {
                      console.log("Dropdown visibility change triggered:", open);
                      if (showTour && stepIndex >= 0) {
                        return;
                      }
                      setIsDropdownOpen(open);
                    }}
                    dropdownStyle={{
                      minWidth: "400px", 
                      padding: "10px",
                      backgroundColor: theme === "dark" ? "#1f1f1f" : "#fff",
                      borderRadius: "8px",
                      boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                    }}
                    optionLabelProp="label"
                  >
                    {Object.entries(finalModelOptions).map(([provider, models]) => {
                      const { name, icon } = botIconsMap[provider] || {
                        name: provider,
                      };

                      return (
                        <Select.OptGroup
                          key={provider}
                          label={
                            <span
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                              }}
                            >
                              {icon && <img src={icon} alt={name} width={16} />}
                              {name}
                            </span>
                          }
                        >
                          {models.map((model) => (
                            <Select.Option
                              key={`${provider}|${model.value}`}
                              value={`${provider}|${model.value}`}
                              label={model.label}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                }}
                              >
                                <strong>{model.label}</strong>
                                <small style={{ color: "#999" }}>
                                  {model.description}
                                </small>
                              </div>
                            </Select.Option>
                          ))}
                        </Select.OptGroup>
                      );
                    })}
                  </Select>
                </Tooltip>
                <Tooltip title="Prompt Template">
                  <Button
                    type="default"
                    icon={
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="1em"
                        height="1.2em"
                        style={{ color: theme === "light" ? "#000" : "#fff" }}
                        className="bi bi-file-earmark-bar-graph"
                        viewBox="0 0 16 16"
                      >
                        <path d="M10 13.5a.5.5 0 0 0 .5.5h1a.5.5 0 0 0 .5-.5v-6a.5.5 0 0 0-.5-.5h-1a.5.5 0 0 0-.5.5v6zM7.5 14a.5.5 0 0 1-.5-.5v-4a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v4a.5.5 0 0 1-.5.5h-1zm-3 0a.5.5 0 0 1-.5-.5v-2a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-.5.5h-1z" />
                        <path d="M14 14V4.5L9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2zM9.5 3A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5v2z" />
                      </svg>
                    }
                    onClick={() => navigate("/templates")}
                    style={{ marginLeft: "0px", borderRadius: "16px", color: theme === "light" ? "rgb(87, 91, 95)" : "#fff" }}
                  >
                    Prompt Templates
                  </Button>
                </Tooltip>
                <div className="brand-voice-whole">
                <span className="select-label" style={{width:"100px", color: theme === "light" ? "rgb(87, 91, 95)" : "#fff" }}>Brand Voice</span>
                <Tooltip title="It will be available soon">
                  <Switch
                    checked={useBrandInfo}
                    className="switch-button"
                    onChange={async (checked) => {
                      setUseBrandInfo(checked);
                      if (checked) {
                        setSelectedCompany(null);
                      }
                      await updateUserCompanyKnowledgeBaseUseFlag(checked)
                    }}
                    popupClassName={`custom-select-dropdown-popup ${theme === "dark" && "dark-mode"}`}
                  />
                </Tooltip>
                </div>
              </div>
              <div className="blurry-box"></div>
            </div>
          )}
          <UsefulPromptDropdown
            isVisible={showPromptDropdown}
            onSelection={handleUsefulPromptSelection}
          >
            <>
              {fetching && <div className="loading-overlay"></div>}
              <textarea
                ref={promptInputRef}
                autoComplete="off"
                placeholder="Ask me anything..."
                name="inputPrompt"
                className={`inputPrompttTextarea ${theme === "dark" && "dark-mode"}`}
                rows="2"
                value={inputPrompt}
                onKeyDown={handleKeyDown}
                onChange={onInputPromptChange}
                style={{ height: "auto", color: theme === "light" ? "rgb(87, 91, 95)" : "#fff" }}
                onInput={(e) => {
                  const textarea = e.target;
                  textarea.style.height = "auto";
                  textarea.style.height = `${textarea.scrollHeight}px`;
                }}
              />
            </>
          </UsefulPromptDropdown>
          <div className="actions-container">
            <Tooltip title="It will be available soon">
              <button className="plus-button" aria-label="Attach" disabled>
                <span className="plusbtn-icon"><PlusOutlined /></span>
              </button>
            </Tooltip>
            <div className="d-flex">
            <Tooltip title={listening ? "Stop recording" : "Start recording"}>
                <button
                  disabled={loading || !browserSupportsSpeechRecognition}
                  aria-label={listening ? "Stop recording" : "Start recording"}
                  className={`sendIcon-outline ${theme === "dark" && "dark-mode"} ${listening ? "mic-active" : ""}`}
                  type="button"
                  onClick={handleMicClick}
                >
                  <AudioOutlined style={{ color: listening ? "#ff4d4f" : "inherit" }} />
                </button>
              </Tooltip>
              <button
                disabled={loading || !inputPrompt.trim()}
                aria-label="Insert separator"
                className={`sendIcon-outline ${theme === "dark" && "dark-mode"}`}
                type="button"
                onClick={() => {
                  setInputPrompt(prev => `${prev}\n----\n`);
                  setShowMassage(true);
                }}
              >
                <UnorderedListOutlined />
              </button>
              <button
                disabled={loading || !inputPrompt.trim()}
                aria-label="form submit"
                className={`sendIcon ${theme === "dark" && "dark-mode"}`}
                type="submit"
              >
                <SendOutlined className="send" />
              </button>
            </div>
          </div>
        </div>
      </form>
      {loading ? (
        <div className="stop-generating-btn-container multi-provider-area-stop-btn">
          <Button
            danger
            type="primary"
            onClick={handleStopGeneratingButton}
            style={{ width: "160px" }}
            icon={<FaRegStopCircle />}
            size={30}
          >
            Stop Generating
          </Button>
        </div>
      ) : null}
      <div
        style={{ marginTop: "20px" }}
        className={`shortcut-text ${theme === "dark" && "dark-mode"}`}
      >
        <Text style={{ color: "#717171" }}>
          Press <span className={`slash-icon ${theme === "dark" && "dark-mode"}`}>/</span> to access a list of task commands.
        </Text>
      </div>
    </div>
  );
};

export default ChatPromptInputForm;